/* eslint-enable */
import React, { useEffect } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import DataTableComponent, { DataTableProps } from './DataTableComponent';
import { OrderDirection } from '../common/enums/order-direction.enum';
import { useErrorToast } from '../hooks/useToast';
import Card from '../layouts/CardLayout';
import LoadingContent from './LoadingContent';
import CellLayout from './CellLayout';
import PageHeader from './PageHeader';
import PageLayout from '../layouts/PageLayout';
import { useAdminStatistics } from '../services/statistics/statistics.service';
import { IAdminStatistics } from '../common/interfaces/admin.interface';
import { AdminDashboardBasicProps } from './AdminDashboardWithQueryParams';


const StatisticsTableFilteredHeader: TableColumn<IAdminStatistics>[] = [
    {
        id: 'client_name',
        name: 'Nume client',
        sortable: true,
        grow: 1,
        cell: (row: IAdminStatistics) => (
            <CellLayout>
                <small id={`client_name__${row.client_id}`} className="cell-layout">
                    {row.client_name}
                </small>
            </CellLayout>
        ),
    },
    {
        id: 'request_count',
        name: 'Numar cereri',
        grow: 1,
        sortable: true,
        cell: (row: IAdminStatistics) => (
            <CellLayout>
                <small
                    id={`request_count__${row.client_id}`}
                    className="cell-layout"
                >{row.request_count}</small>
            </CellLayout>
        ),
    },
    {
        id: 'total_months',
        name: 'Status Luni introduse',
        grow: 1,
        sortable: true,
        cell: (row: IAdminStatistics) => (
            <CellLayout>
                <small
                    id={`total_months__${row.client_id}`}
                    className="cell-layout"
                >{`${row.fulfilled_entries}/${row.total_months}`}</small>
            </CellLayout>
        ),
    },
    {
        id: 'avg_entries_per_request',
        name: 'Numar mediu de intrari per cerere',
        grow: 1,
        sortable: true,
        cell: (row: IAdminStatistics) => (
            <CellLayout>
                <small
                    id={`avg_entries_per_request__${row.client_id}`}
                    className="cell-layout"
                >{row.avg_entries_per_request}</small>
            </CellLayout>
        ),
    },
];

type AdminDashboardTableType<T extends object> = React.FC<AdminDashboardBasicProps> & {
    DataTableComponent: React.FC<DataTableProps<T>>;
};

const AdminDashboard: AdminDashboardTableType<IAdminStatistics> = ({
    query,
    setQuery,
}: AdminDashboardBasicProps) => {

    const { data: statistics, error: statisticsError, isLoading: isStatisticsLoading } = useAdminStatistics(
        query?.limit as number,
        query?.page as number,
        query?.search,
        query?.orderBy,
        query?.orderDirection as OrderDirection,
    );

    useEffect(() => {
        if (statisticsError) {
            useErrorToast('Error retrieving data.');
        }
    }, [statisticsError]);

    const onChangeRowsPerPage = (limit: number) => {
        setQuery({ limit, page: 1 }, 'replaceIn');
    };

    const onChangePage = (page: number) => {
        setQuery({ page }, 'replaceIn');
    };

    const onSort = (column: TableColumn<IAdminStatistics>, direction: SortOrder) => {
        setQuery({
            orderBy: column.id as string,
            orderDirection:
                direction.toLocaleUpperCase() === OrderDirection.ASC
                    ? OrderDirection.ASC
                    : OrderDirection.DESC,
        }, 'replaceIn');
    };

    return (
        <PageLayout>
            <div className='flex gap-4 justify-between w-full'>
                <PageHeader
                    title="Panou de bord"
                />
            </div>
            <Card>
                <AdminDashboard.DataTableComponent
                    columns={[
                        ...StatisticsTableFilteredHeader,
                    ] as TableColumn<IAdminStatistics>[]}
                    pagination
                    onSort={onSort}
                    data={statistics?.items}
                    onChangePage={onChangePage}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                    pointerOnHover={true}
                    paginationDefaultPage={query.page}
                    paginationPerPage={query.limit}
                    paginationTotalRows={statistics?.meta.totalItems}
                    defaultSortAsc={false}
                    loading={isStatisticsLoading}
                />
                {isStatisticsLoading && <LoadingContent />}
            </Card>
        </PageLayout>
    );
};

AdminDashboard.DataTableComponent = DataTableComponent;

export default AdminDashboard;
